import { Document, Packer, Paragraph, TextRun, HeadingLevel, PageBreak, AlignmentType, Header } from 'docx';
import type { ISectionOptions } from 'docx';
import { saveAs } from 'file-saver';
import type { Novel } from './store';

/**
 * Strips HTML and markdown formatting to return plain text.
 */
function stripFormatting(htmlContent: string): string {
    // Replace paragraph endings with double newlines
    let text = htmlContent.split('</p>').join('\n\n').split('<br>').join('\n\n').split('<br />').join('\n\n');
    // Strip all other HTML tags
    text = text.replace(/<[^>]*>?/gm, '');
    // Decode common HTML entities (if needed)
    text = text.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&');
    return text.trim();
}

/**
 * Helper to split text body into docx paragraphs
 */
function parseTextToParagraphs(text: string): Paragraph[] {
    const lines = text.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    return lines.map(line => new Paragraph({
        children: [
            new TextRun({
                text: line,
                font: 'Times New Roman',
                size: 24, // 12pt (measured in half-points)
            })
        ],
        spacing: {
            line: 480, // Double spacing
            before: 0,
            after: 0
        },
        indent: {
            firstLine: 720 // 0.5 inch indent (measured in twips, 1440 twips = 1 inch)
        }
    }));
}

/**
 * 1. Export as Industry Standard DOCX (Manuscript Format)
 */
export async function exportToDocx(novel: Novel, authorName: string = 'Author Name'): Promise<void> {
    const sections: ISectionOptions[] = [];
    const sortedChapters = [...novel.chapters].sort((a, b) => a.order - b.order);

    // Title Page (Optional styling, kept simple for now)
    sections.push({
        properties: {
            page: {
                margin: {
                    top: 1440, // 1 inch
                    right: 1440,
                    bottom: 1440,
                    left: 1440,
                },
            },
        },
        children: [
            new Paragraph({
                children: [
                    new TextRun({ text: '', break: 10 }), // Push down the page
                    new TextRun({
                        text: novel.title.toUpperCase(),
                        bold: true,
                        size: 36, // 18pt
                        font: 'Times New Roman',
                    })
                ],
                alignment: AlignmentType.CENTER,
            }),
            new Paragraph({
                children: [
                    new TextRun({
                        text: `by ${authorName}`,
                        size: 24,
                        font: 'Times New Roman',
                    })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 240 }
            }),
            new Paragraph({
                children: [
                    new TextRun({
                        text: `Word Count: ${novel.chapters.reduce((sum, ch) => sum + (ch.wordCount || 0), 0).toLocaleString()}`,
                        size: 24,
                        font: 'Times New Roman',
                    }),
                    new PageBreak()
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 240 }
            })
        ]
    });

    // Content Pages
    const contentChildren: any[] = [];
    let isFirstChapter = true;

    for (const chapter of sortedChapters) {
        if (!isFirstChapter) {
            contentChildren.push(new Paragraph({ children: [new PageBreak()] }));
        }

        // Chapter Header
        contentChildren.push(
            new Paragraph({
                heading: HeadingLevel.HEADING_1,
                alignment: AlignmentType.CENTER,
                spacing: { before: 1440, after: 720 }, // Drop down 1 inch, space after
                children: [
                    new TextRun({
                        text: chapter.title.toUpperCase(),
                        bold: true,
                        size: 28, // 14pt
                        font: 'Times New Roman'
                    })
                ]
            })
        );

        // Chapter Body
        const plainText = stripFormatting(chapter.content);
        const bodyParagraphs = parseTextToParagraphs(plainText);
        contentChildren.push(...bodyParagraphs);

        isFirstChapter = false;
    }

    const cleanTitle = novel.title.replace(/\\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');

    sections.push({
        headers: {
            default: new Header({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.RIGHT,
                        children: [
                            new TextRun({
                                text: `${authorName} / ${novel.title.toUpperCase()} / `,
                                font: 'Times New Roman',
                                size: 24
                            }),
                            new TextRun({
                                children: ["PageNumber" as any], // Workaround for simple docx pageref
                                font: 'Times New Roman',
                                size: 24
                            })
                        ]
                    })
                ]
            })
        },
        properties: {
            page: {
                pageNumbers: { start: 1, formatType: "decimal" },
            }
        },
        children: contentChildren
    });

    const doc = new Document({
        creator: authorName,
        title: novel.title,
        description: 'Manuscript generated by Write Library',
        sections: sections
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${cleanTitle}_Manuscript.docx`);
}

/**
 * 2. Export as PDF (Web Print approach)
 * The simplest and highest fidelity way to PDF in the browser is triggering window.print()
 * on a hidden iframe styled like a manuscript. We'll leave the implementation stubbed 
 * to be piped into a print preview or jsPDF if necessary.
 */
export function exportToPDF(_novel: Novel) {
    alert("To export as PDF, open Manuscript Mode and use your browser's Print to PDF (Cmd/Ctrl + P).");
}

/**
 * 3. Export as Plain Text (Fallback)
 */
export function exportToPlainText(novel: Novel): void {
    const sortedChapters = [...novel.chapters].sort((a, b) => a.order - b.order);
    let output = `${novel.title.toUpperCase()}\n\n`;

    for (const chapter of sortedChapters) {
        output += `\n\n### ${chapter.title} ###\n\n`;
        output += stripFormatting(chapter.content);
        output += `\n\n---`;
    }

    const blob = new Blob([output], { type: 'text/plain;charset=utf-8' });
    const cleanTitle = novel.title.replace(/\\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    saveAs(blob, `${cleanTitle}.txt`);
}
